import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  IconButton,
  makeStyles,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Alert from '@material-ui/lab/Alert';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mapTestCaseThreadVersions, TestCaseVersion } from '../version-history';
import {
  acceptPendingInstance,
  discardPendingInstance,
  getPendingTestCaseInstances,
  rejectPendingInstance,
} from './instanceReviewApi';
import { InstanceReviewDiffSection } from './InstanceReviewDiffSection';
import { InstanceReviewHistorySection } from './InstanceReviewHistorySection';
import type { TestCaseInstance } from './types';
import { getTestCaseThread } from '../api';
import type { TestCaseThreadStatus } from '../types';
import { ThreadReviewSection } from '../thread-review';

interface InstanceReviewPanelProps {
  open: boolean;
  testCaseThreadId: string | null;
  testCaseId: string | null;
  testCaseTitle?: string;
  requirementId?: string;
  requirementName?: string;
  initialThreadStatus?: TestCaseThreadStatus;
  onThreadStatusChange?: (status: TestCaseThreadStatus) => void;
  onDeleted?: () => void;
  onClose: () => void;
  onRefresh?: () => Promise<void>;
}

type ExpandedSection = 'diff' | 'history' | false;

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    width: 'min(1500px, 96vw)',
    maxWidth: 1500,
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
  },
  dialogTitle: {
    flex: '0 0 auto',
    paddingRight: theme.spacing(1),
  },
  dialogTitleContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  dialogHeading: {
    fontWeight: 700,
  },
  content: {
    flex: '1 1 auto',
    overflowY: 'auto',
  },
  summaryCard: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  summaryTitle: {
    fontWeight: 600,
  },
  metadata: {
    color: theme.palette.text.secondary,
  },
  panel: {
    marginBottom: theme.spacing(2),

    '&:before': {
      display: 'none',
    },
  },
  panelSummary: {
    backgroundColor: theme.palette.background.default,
    minHeight: 72,
  },
  sectionTitle: {
    fontWeight: 600,
  },
  panelDetails: {
    display: 'block',
    padding: theme.spacing(2),
  },
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 240,
  },
  actions: {
    flex: '0 0 auto',
  },
  closeButton: {
    minWidth: 96,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.grey[700],

    '&:hover': {
      backgroundColor: theme.palette.grey[800],
    },

    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
  tabs: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabPanel: {
    paddingTop: theme.spacing(1),
  },
}));

function sortPendingNewestFirst(
  pending: TestCaseInstance[],
): TestCaseInstance[] {
  return [...pending].sort((left, right) => {
    if (right.versionNumber !== left.versionNumber) {
      return right.versionNumber - left.versionNumber;
    }

    const rightDate = Date.parse(right.createdAt);
    const leftDate = Date.parse(left.createdAt);

    return (
      (Number.isNaN(rightDate) ? 0 : rightDate) -
      (Number.isNaN(leftDate) ? 0 : leftDate)
    );
  });
}

export const InstanceReviewPanel = ({
  open,
  testCaseThreadId,
  testCaseId,
  testCaseTitle,
  requirementId,
  requirementName,
  initialThreadStatus = 'DRAFT',
  onThreadStatusChange,
  onDeleted,
  onClose,
  onRefresh,
}: InstanceReviewPanelProps) => {
  const classes = useStyles();
  const { t } = useTranslation('testCases');

  const [current, setCurrent] = useState<TestCaseInstance | null>(null);

  const [pending, setPending] = useState<TestCaseInstance[]>([]);

  const [versions, setVersions] = useState<TestCaseVersion[]>([]);

  const [selectedPendingId, setSelectedPendingId] = useState<string | null>(
    null,
  );

  const [expandedSection, setExpandedSection] =
    useState<ExpandedSection>(false);

  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState(false);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [currentAction, setCurrentAction] = useState<
    'accept' | 'reject' | 'regenerate' | null
  >(null);

  const [activeTab, setActiveTab] = useState(0);

  const [threadStatus, setThreadStatus] =
    useState<TestCaseThreadStatus>(initialThreadStatus);

  const [threadProcessing, setThreadProcessing] = useState(false);

  const loadReview = useCallback(async () => {
    if (!testCaseThreadId || !testCaseId) {
      return;
    }

    setLoading(true);
    setLoadError(false);

    try {
      const [pendingResponse, thread] = await Promise.all([
        getPendingTestCaseInstances(testCaseThreadId),
        getTestCaseThread(testCaseThreadId),
      ]);

      const versionResponse = mapTestCaseThreadVersions(thread, testCaseId);

      const sortedPending = sortPendingNewestFirst(pendingResponse.pending);

      setCurrent(pendingResponse.current);
      setPending(sortedPending);
      setVersions(versionResponse.versions);

      setSelectedPendingId(previous => {
        if (
          previous &&
          sortedPending.some(instance => instance.instanceId === previous)
        ) {
          return previous;
        }

        return sortedPending[0]?.instanceId ?? null;
      });

      setExpandedSection(previous => {
        if (sortedPending.length === 0) {
          return previous === 'history' ? 'history' : false;
        }

        return previous || 'diff';
      });

      setThreadStatus(thread.status);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [testCaseId, testCaseThreadId]);

  useEffect(() => {
    if (!open) {
      setCurrent(null);
      setPending([]);
      setVersions([]);
      setSelectedPendingId(null);
      setExpandedSection(false);
      setLoadError(false);
      setActionError(false);
      setSuccessMessage(null);
      setProcessing(false);
      setCurrentAction(null);
      setActiveTab(0);
      setThreadStatus(initialThreadStatus);
      setThreadProcessing(false);
      return;
    }

    void loadReview();
  }, [initialThreadStatus, loadReview, open]);

  const handleSectionChange =
    (section: Exclude<ExpandedSection, false>) =>
    (_event: React.ChangeEvent<unknown>, expanded: boolean) => {
      setExpandedSection(expanded ? section : false);
    };

  const runAction = async (
    actionType: 'accept' | 'reject' | 'regenerate',
    action: (threadId: string, instanceId: string) => Promise<void>,
    successKey: string,
  ) => {
    if (!testCaseThreadId || !selectedPendingId || processing) {
      return;
    }

    setProcessing(true);
    setActionError(false);
    setCurrentAction(actionType);

    try {
      await action(testCaseThreadId, selectedPendingId);
      await loadReview();
      if (onRefresh) {
        await onRefresh();
      }
      setSuccessMessage(t(successKey));
    } catch {
      setActionError(true);
    } finally {
      setProcessing(false);
      setCurrentAction(null);
    }
  };

  const handleAccept = () =>
    runAction(
      'accept',
      acceptPendingInstance,
      'testCaseGeneration.instanceReview.messages.accepted',
    );

  const handleReject = () =>
    runAction(
      'reject',
      rejectPendingInstance,
      'testCaseGeneration.instanceReview.messages.rejected',
    );

  const handleRegenerate = () =>
    runAction(
      'regenerate',
      discardPendingInstance,
      'testCaseGeneration.instanceReview.messages.regenerated',
    );

  const handleThreadStatusChange = (status: TestCaseThreadStatus) => {
    setThreadStatus(status);
    onThreadStatusChange?.(status);

    if (status === 'APPROVED') {
      setPending([]);
      setSelectedPendingId(null);
      setExpandedSection(false);
    }
  };

  const handleThreadDeleted = () => {
    onDeleted?.();
    onClose();
  };

  let content;

  if (loading) {
    content = (
      <Box className={classes.centered}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (loadError) {
    content = (
      <Alert severity="error">
        {t('testCaseGeneration.instanceReview.messages.loadFailed')}
      </Alert>
    );
  } else {
    content = (
      <>
        <Card variant="outlined" className={classes.summaryCard}>
          <CardContent>
            <Typography variant="h6" className={classes.summaryTitle}>
              {testCaseTitle ??
                t('testCaseGeneration.instanceReview.titleFallback')}
            </Typography>

            {(requirementId || requirementName) && (
              <Typography variant="body2" className={classes.metadata}>
                {t('testCaseGeneration.instanceReview.requirementMetadata', {
                  id: requirementId ?? '—',
                  name: requirementName ?? '—',
                })}
              </Typography>
            )}

            <Typography variant="caption" className={classes.metadata}>
              {t('testCaseGeneration.instanceReview.testCaseMetadata', {
                id: testCaseId ?? '—',
                threadId: testCaseThreadId ?? '—',
              })}
            </Typography>
          </CardContent>
        </Card>

        <Tabs
          value={activeTab}
          onChange={(_event, value) => setActiveTab(value)}
          className={classes.tabs}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={t('testCaseGeneration.instanceReview.tabs.instance')} />

          <Tab label={t('testCaseGeneration.instanceReview.tabs.thread')} />
        </Tabs>
        {activeTab === 0 && (
          <Box className={classes.tabPanel}>
            <ExpansionPanel
              expanded={expandedSection === 'diff'}
              onChange={handleSectionChange('diff')}
              className={classes.panel}
            >
              <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.panelSummary}
              >
                <Box>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {t('testCaseGeneration.instanceReview.diffTitle')}
                  </Typography>

                  <Typography variant="body2" color="textSecondary">
                    {t('testCaseGeneration.instanceReview.diffDescription')}
                  </Typography>
                </Box>
              </ExpansionPanelSummary>

              <ExpansionPanelDetails className={classes.panelDetails}>
                <InstanceReviewDiffSection
                  current={current}
                  pending={pending}
                  selectedPendingId={selectedPendingId}
                  processing={processing}
                  currentAction={currentAction}
                  onSelectPending={setSelectedPendingId}
                  onAccept={handleAccept}
                  onReject={handleReject}
                  onRegenerate={handleRegenerate}
                />
              </ExpansionPanelDetails>
            </ExpansionPanel>

            <ExpansionPanel
              expanded={expandedSection === 'history'}
              onChange={handleSectionChange('history')}
              className={classes.panel}
            >
              <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.panelSummary}
              >
                <Box>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {t('testCaseGeneration.instanceReview.history.title')}
                  </Typography>

                  <Typography variant="body2" color="textSecondary">
                    {t('testCaseGeneration.instanceReview.history.description')}
                  </Typography>
                </Box>
              </ExpansionPanelSummary>

              <ExpansionPanelDetails className={classes.panelDetails}>
                <InstanceReviewHistorySection versions={versions} />
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {actionError && (
              <Alert severity="error">
                {t('testCaseGeneration.instanceReview.messages.actionFailed')}
              </Alert>
            )}
          </Box>
        )}
        {activeTab === 1 && testCaseThreadId && (
          <Box className={classes.tabPanel}>
            <ThreadReviewSection
              threadId={testCaseThreadId}
              status={threadStatus}
              current={current}
              onStatusChange={handleThreadStatusChange}
              onDeleted={handleThreadDeleted}
              onBusyChange={setThreadProcessing}
            />
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={processing || threadProcessing ? undefined : onClose}
        maxWidth={false}
        PaperProps={{
          className: classes.dialogPaper,
        }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.dialogTitleContent}>
            <Typography variant="h5" className={classes.dialogHeading}>
              {t('testCaseGeneration.instanceReview.title')}
            </Typography>

            <IconButton
              size="small"
              disabled={processing || threadProcessing}
              aria-label={t('testCaseGeneration.instanceReview.close')}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers className={classes.content}>
          {content}
        </DialogContent>

        <DialogActions className={classes.actions}>
          <Button
            className={classes.closeButton}
            variant="contained"
            disabled={processing || threadProcessing}
            onClick={onClose}
          >
            {t('testCaseGeneration.instanceReview.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successMessage !== null}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InstanceReviewPanel;
